import React, { useMemo } from 'react';
import { StepProps, FormData } from '../../types';
import { Input } from '../ui/Input';
import { RadioGroup } from '../ui/RadioGroup';
import { Button } from '../ui/Button'; 
import {
  calculateTaxableIncomeBeforeLossUtilisation,
  calculateTaxableIncomeAfterTaxLoss,
  calculateCTLiabilityBeforeCredits,
  calculateCorporateTaxPayable,
} from '../../utils/calculations';

export const Step7TaxLiability: React.FC<StepProps> = ({ formData, onChange, errors, t, openScheduleModal }) => {
  const data = formData.taxLiability;

  const taxableIncomeBeforeLossUtilisation = useMemo(
    () => calculateTaxableIncomeBeforeLossUtilisation(formData),
    [formData]
  );

  const taxableIncomeAfterTaxLossAdjustments = useMemo(
    () => calculateTaxableIncomeAfterTaxLoss(taxableIncomeBeforeLossUtilisation, data.taxLossesUtilisedCurrentPeriod),
    [taxableIncomeBeforeLossUtilisation, data.taxLossesUtilisedCurrentPeriod]
  );

  const corporateTaxLiabilityBeforeCredits = useMemo(
    () => calculateCTLiabilityBeforeCredits(
            taxableIncomeAfterTaxLossAdjustments, 
            formData.elections, 
            formData.taxablePersonDetails,
            formData.freeZoneSchedule
          ),
    [taxableIncomeAfterTaxLossAdjustments, formData.elections, formData.taxablePersonDetails, formData.freeZoneSchedule]
  );

  const corporateTaxPayable = useMemo(
    () => calculateCorporateTaxPayable(corporateTaxLiabilityBeforeCredits, data.taxCreditsApplied),
    [corporateTaxLiabilityBeforeCredits, data.taxCreditsApplied]
  );
  

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-[#1a2c46] mb-6">{t('step7_title')}</h2>

      <fieldset className="p-4 border rounded-md bg-slate-50">
        <legend className="text-md font-semibold text-[#1a2c46] px-2">{t('s10_1_taxable_income_title')}</legend>
        <Input
          label={t('s10_1_taxable_income_loss_display_label')}
          type="text"
          id="taxableIncomeLossBeforeTaxLossUtilisation"
          value={taxableIncomeBeforeLossUtilisation.toFixed(2)}
          readOnly
          className="bg-gray-100"
        />
      </fieldset>

      <fieldset className="p-4 border rounded-md bg-slate-50">
        <legend className="text-md font-semibold text-[#1a2c46] px-2">{t('s10_2_tax_losses_title')}</legend>
        <RadioGroup
          label={t('s10_2_has_brought_forward_losses_label')}
          name="hasBroughtForwardTaxLosses"
          value={data.hasBroughtForwardTaxLosses}
          onChange={(val) => onChange('taxLiability', 'hasBroughtForwardTaxLosses', val)}
          options={[{ label: t('yes'), value: 'yes' }, { label: t('no'), value: 'no' }]}
          error={errors.hasBroughtForwardTaxLosses as string}
          t={t}
        />
        <RadioGroup label={t('s10_2_claim_surrender_loss_label')} name="claimOrSurrenderTaxLossesGroupEntity" value={data.claimOrSurrenderTaxLossesGroupEntity || ''} onChange={(val) => onChange('taxLiability', 'claimOrSurrenderTaxLossesGroupEntity', val)} options={[{ label: t('yes'), value: 'yes' }, { label: t('no'), value: 'no' }]} t={t} error={errors.claimOrSurrenderTaxLossesGroupEntity as string} />
        {(data.hasBroughtForwardTaxLosses === 'yes' || data.claimOrSurrenderTaxLossesGroupEntity === 'yes') && openScheduleModal && (
           <Button 
            type="button" 
            onClick={() => {
                if (formData.taxablePersonDetails.filingAs === 'tax_group_parent') {
                    openScheduleModal('schedule17TaxGroupLosses', formData.schedule17TaxGroupLosses)
                } else {
                    openScheduleModal('schedule17TaxLosses', formData.schedule17TaxLosses)
                }
            }}
            variant="secondary"
            className="mt-2 mb-2 text-sm"
          >
            {t('open_schedule_button', { schedule_name: formData.taxablePersonDetails.filingAs === 'tax_group_parent' ? t('sch17_B_title') : t('schedule17_title')})}
          </Button>
        )}
        <Input
            label={t('s10_2_losses_utilised_label')}
            type="text" 
            id="taxLossesUtilisedCurrentPeriod"
            name="taxLossesUtilisedCurrentPeriod"
            value={data.taxLossesUtilisedCurrentPeriod} 
            readOnly 
            className="bg-gray-100"
            error={errors.taxLossesUtilisedCurrentPeriod as string}
            placeholder={t('amount_aed_placeholder')}
            helpText={t('s10_2_losses_utilised_help')}
          />
        {formData.taxablePersonDetails.filingAs !== 'tax_group_parent' && (
            <Input label={t('s10_2_tax_losses_claimed_other_entities_label')} type="text" id="taxLossesClaimedFromOtherGroupEntities" value={data.taxLossesClaimedFromOtherGroupEntities || '0.00'} readOnly className="bg-gray-100" placeholder={t('amount_aed_placeholder')} />
        )}
        {formData.taxablePersonDetails.filingAs === 'tax_group_parent' && (
            <>
                <Input label={t('s10_3_pre_grouping_losses_utilised_label')} type="text" id="preGroupingTaxLossesUtilisedTaxGroup" value={data.preGroupingTaxLossesUtilisedTaxGroup || '0.00'} readOnly className="bg-gray-100" placeholder={t('amount_aed_placeholder')} />
                <Input label={t('s10_3_losses_claimed_other_entities_label')} type="text" id="taxLossesClaimedFromOtherGroupEntitiesTaxGroup" value={data.taxLossesClaimedFromOtherGroupEntitiesTaxGroup || '0.00'} readOnly className="bg-gray-100" placeholder={t('amount_aed_placeholder')} />
            </>
        )}
         <Input
          label={t('s10_2_taxable_income_after_loss_label')}
          type="text"
          id="taxableIncomeAfterTaxLossAdjustments"
          value={taxableIncomeAfterTaxLossAdjustments.toFixed(2)}
          readOnly
          className="bg-gray-100"
        />
      </fieldset>

      <fieldset className="p-4 border rounded-md bg-slate-50">
        <legend className="text-md font-semibold text-[#1a2c46] px-2">{t('s10_4_tax_calc_credits_title')}</legend>
        <Input
          label={t('s10_4_ct_liability_before_credits_label')}
          type="text"
          id="corporateTaxLiabilityBeforeCredits"
          value={corporateTaxLiabilityBeforeCredits.toFixed(2)}
          readOnly
          className="bg-gray-100"
          helpText={t('s10_4_ct_liability_before_credits_help')}
        />
        <RadioGroup
          label={t('s10_4_wish_to_use_credits_label')}
          name="wishToUseTaxCredits"
          value={data.wishToUseTaxCredits}
          onChange={(val) => onChange('taxLiability', 'wishToUseTaxCredits', val)}
          options={[{ label: t('yes'), value: 'yes' }, { label: t('no'), value: 'no' }]}
          error={errors.wishToUseTaxCredits as string}
          t={t}
        />
        {data.wishToUseTaxCredits === 'yes' && openScheduleModal && (
          <Button 
            type="button" 
            onClick={() => openScheduleModal('schedule15TaxCredits', formData.schedule15TaxCredits)}
            variant="secondary"
            className="mt-2 mb-2 text-sm"
          >
             {t('open_schedule_button', { schedule_name: t('schedule15_title')})}
          </Button>
        )}
         <Input
            label={t('s10_4_tax_credits_applied_label')}
            type="text" 
            id="taxCreditsApplied"
            name="taxCreditsApplied"
            value={data.taxCreditsApplied} 
            readOnly 
            className="bg-gray-100"
            error={errors.taxCreditsApplied as string}
            placeholder={t('amount_aed_placeholder')}
            helpText={t('s10_4_tax_credits_applied_help')}
          />
        <Input
          label={t('s10_4_corporate_tax_payable_label')}
          type="text"
          id="corporateTaxPayable"
          value={corporateTaxPayable.toFixed(2)}
          readOnly
          className="bg-gray-100 font-bold text-lg"
        />
      </fieldset>

      <fieldset className="p-4 border rounded-md bg-slate-50">
        <legend className="text-md font-semibold text-[#1a2c46] px-2">{t('s10_5_estimated_figures_title')}</legend>
        <RadioGroup
          label={t('s10_5_estimated_figures_included_label')}
          name="estimatedFiguresIncludedInReturn"
          value={data.estimatedFiguresIncludedInReturn}
          onChange={(val) => onChange('taxLiability', 'estimatedFiguresIncludedInReturn', val)}
          options={[{ label: t('yes'), value: 'yes' }, { label: t('no'), value: 'no' }]}
          error={errors.estimatedFiguresIncludedInReturn as string}
          t={t}
        />
        {data.estimatedFiguresIncludedInReturn === 'yes' && (
          <Input
            label={t('s10_5_details_label')}
            type="text"
            id="estimatedFiguresDetails"
            name="estimatedFiguresDetails"
            value={data.estimatedFiguresDetails}
            onChange={(e) => onChange('taxLiability', 'estimatedFiguresDetails', e.target.value)}
            error={errors.estimatedFiguresDetails as string}
            placeholder={t('s10_5_details_placeholder')}
          />
        )}
      </fieldset>

      <p className="mt-4 text-sm text-gray-600">{t('step7_note')}</p>
      {formData.elections.smallBusinessRelief && (
         <p className="mt-2 p-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-md text-sm">
           {t('sbr_skip_note')}
         </p>
      )}
    </div>
  );
};